import { AxiosRequestConfig } from 'axios';
import axios from './axios';

export interface CardPageRenderData {
	imageUrl: string;
	durationSeconds: number;
	embeddedVideos?: Array<{
		url: string;
		x: number;
		y: number;
		width: number;
		height: number;
		startTime?: number;
	}>;
	embeddedGifs?: Array<{
		url: string;
		x: number;
		y: number;
		width: number;
		height: number;
	}>;
}

export interface BoardTileRenderData {
	type: 'video' | 'text' | 'image' | 'gif';
	content: Record<string, any>;
	order: number;
}

export interface VideoRenderConfig {
	cardPages: CardPageRenderData[];
	videoSubmissions: Array<{ url: string; order: number }>;
	boardTiles?: BoardTileRenderData[];
	music?: { url: string; volume: number };
	outputFormat: 'mp4';
	resolution: { width: number; height: number };
}

export interface VideoRenderStatus {
	status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
	progress?: number;
	outputUrl?: string;
	error?: string;
	step?: string;
}

const triggerRender = (cardId: string, config: VideoRenderConfig, userId: string): Promise<{ renderId: string; status: 'QUEUED' }> => {
	return new Promise(async (resolve, reject) => {
		try {
			const axiosConfig: AxiosRequestConfig = {
				method: 'post',
				url: `/video-render/${cardId}`,
				data: { ...config, userId },
			};
			const resp = await axios(axiosConfig);
			resolve(resp.data.data);
		} catch (error) {
			reject(error);
		}
	});
};

const getRenderStatus = (cardId: string): Promise<VideoRenderStatus> => {
	return new Promise(async (resolve, reject) => {
		try {
			const axiosConfig: AxiosRequestConfig = {
				method: 'get',
				url: `/video-render/${cardId}/status`,
			};
			const resp = await axios(axiosConfig);
			resolve(resp.data.data);
		} catch (error) {
			reject(error);
		}
	});
};

type StreamCallbacks = {
	onEvent: (payload: Record<string, unknown>) => void;
	onError?: (error: unknown) => void;
};

const streamVideoProgress = (renderId: string, workspaceId: string, { onEvent, onError }: StreamCallbacks) => {
	const controller = new AbortController();
	const baseURL = axios.defaults.baseURL ?? '';
	const trimmedBase = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
	const url = `${trimmedBase}/api/stream/video/${renderId}`;
	const headers: Record<string, string> = {};
	if (workspaceId) headers['x-workspace-id'] = workspaceId;
	const authHeader = axios.defaults.headers.common?.Authorization;
	if (authHeader) headers['Authorization'] = String(authHeader);

	const connect = async () => {
		try {
			const response = await fetch(url, {
				headers,
				signal: controller.signal,
			});

			if (!response.ok || !response.body) {
				throw new Error(`Stream request failed with status ${response.status}`);
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let buffer = '';

			while (true) {
				const { value, done } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });
				const segments = buffer.split('\n\n');
				buffer = segments.pop() ?? '';
				for (const segment of segments) {
					const lines = segment.split('\n');
					for (const line of lines) {
						if (line.startsWith('data:')) {
							const data = line.replace(/^data:\s*/, '');
							if (!data) continue;
							try {
								const payload = JSON.parse(data);
								onEvent(payload);
							} catch {
								// skip unparseable payloads
							}
						}
					}
				}
			}
		} catch (streamError) {
			if ((streamError as DOMException)?.name === 'AbortError') {
				// intentional abort
			} else {
				console.error('Video progress stream error:', streamError);
				if (onError) onError(streamError);
			}
		}
	};

	void connect();

	return () => {
		controller.abort();
	};
};

const sendVideoCard = (cardId: string, recipientEmail: string, recipientName?: string): Promise<{ message: string }> => {
	return new Promise(async (resolve, reject) => {
		try {
			const axiosConfig: AxiosRequestConfig = {
				method: 'post',
				url: `/video-render/${cardId}/send`,
				data: { recipientEmail, recipientName },
			};
			const resp = await axios(axiosConfig);
			resolve(resp.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const videoRenderService = {
	triggerRender,
	getRenderStatus,
	streamVideoProgress,
	sendVideoCard,
};
